/**
 * Performance screens (KPI + attribution), shared by the customer portal (/performance) and the agency per-client
 * view (partners /clients/:id/performance). Lives outside routes/ so both surfaces can use it without a
 * cross-surface import. Pure helpers + the BFF source; no fabricated numbers anywhere: a value the API did not
 * compute is null and renders as "Not measured".
 */
import { bff, type FetchLike } from '../lib/bff';

export type RangePreset = '7d' | '30d' | '90d' | 'mtd';
export const RANGE_PRESETS: readonly RangePreset[] = ['7d', '30d', '90d', 'mtd'];
export interface Range { fromDay: string; toDay: string; fromIso: string; toIso: string }

const DAY = 86_400_000;
const day = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/** KPI queries take inclusive days; channels/funnel take ISO instants over [from, to + 1 day). UTC days. */
export function rangeFor(preset: RangePreset, now: Date): Range {
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const start = preset === 'mtd' ? Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)
    : today - ((preset === '7d' ? 7 : preset === '30d' ? 30 : 90) - 1) * DAY;
  return { fromDay: day(start), toDay: day(today), fromIso: new Date(start).toISOString(), toIso: new Date(today + DAY).toISOString() };
}

// ---------- API shapes (mirror @qm/contracts/attribution responses) ----------
export interface Totals {
  currency: string; spendMinor: number; impressions: number; clicks: number; leads: number | null; conversions: number | null; revenueMinor: number | null;
  cpcMinor: number | null; cplMinor: number | null; conversionRate: number | null; roas: number | null;
}
export interface Kpis { totals: Totals[]; series: (Totals & { period?: string })[]; byChannel: (Totals & { channel?: string })[] }
export interface FreshnessView {
  asOf: string | null; lagSeconds: number | null; targetSeconds: number; stale: boolean; partial: boolean;
  banners: string[]; sources: { source: string; status: 'ok' | 'failed'; lastEventAt: string | null; lastErrorCode: string | null }[];
}
export interface ChannelSplit { channel: string; currency: string | null; records: number; payingCustomers: number; observedRevenueMinor: number | null; modelledRevenueMinor: number | null }
export interface RecordView {
  id: string; subjectRef: string; channel: string | null; campaignRef: string | null; method: 'observed' | 'modelled'; model: string | null;
  clickIdType: string | null; firstAt: string; lastAt: string; revenueMinor: number | null; currency: string | null;
}
export interface StepView { stage: string; refId: string; sourceModule: string; occurredAt: string; amountMinor: number | null; currency: string | null; basis: 'observed' | 'modelled' }
export interface RecordDetail extends RecordView { steps: StepView[] }
export interface FunnelView {
  from: string; to: string;
  stages: { visits: number; checkouts: number; paidConversions: number; reportAccess: number };
  paidRevenue: { currency: string; netMinor: number; chargesMinor: number; refundsMinor: number }[];
  excluded: { testMode: number; free: number; complimentary: number };
  reconciliation: { ledgerPaidOrders: number; funnelPaidOrders: number; matched: boolean };
}

export type MetricKey = 'spendMinor' | 'cpcMinor' | 'cplMinor' | 'conversionRate' | 'revenueMinor' | 'roas';
export const TILE_METRICS: readonly MetricKey[] = ['spendMinor', 'cpcMinor', 'cplMinor', 'conversionRate', 'revenueMinor', 'roas'];

/** null = not measured (the API had no denominator, or no row at all). Never coerced to 0. */
export function metricValue(t: Totals | undefined, key: MetricKey): number | null {
  if (!t) return null;
  const v = t[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

export type ConnectionState = 'none' | 'partial' | 'stale' | 'ok';
export function connectionState(f: FreshnessView): ConnectionState {
  if (!f.sources.length) return 'none';
  if (f.partial) return 'partial';
  if (f.stale) return 'stale';
  return 'ok';
}

export function pctWidth(value: number, max: number): number {
  if (!(max > 0) || !(value > 0)) return 0;
  return Math.min(100, Math.round((value / max) * 100));
}

// ---------- BFF source ----------
export type Scope = { kind: 'tenant' } | { kind: 'agency'; clientId: string };
export interface Envelope<T, M = Record<string, unknown>> { data: T; meta?: M }

export interface PerformanceSource {
  kpis(r: Range): Promise<Envelope<Kpis>>;
  freshness(): Promise<Envelope<FreshnessView>>;
  channels(r: Range): Promise<Envelope<ChannelSplit[]>>;
  records(q: { limit?: number; cursor?: string | null }): Promise<Envelope<RecordView[], { nextCursor: string | null }>>;
  record(id: string): Promise<Envelope<RecordDetail>>;
  funnel(r: Range): Promise<Envelope<FunnelView>>;
}

const qs = (q: Record<string, string | number | null | undefined>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
};

/**
 * Goes through the shared `bff()` client (same-origin /bff, session observer, BffError), capturing the
 * envelope's `meta` (freshness, keyset cursor) that `bff()` itself does not return.
 */
export function createPerformanceSource(scope: Scope, fetchFn: FetchLike = (i, r) => fetch(i, r)): PerformanceSource {
  const base = scope.kind === 'agency' ? `/v1/attribution/agency/clients/${encodeURIComponent(scope.clientId)}` as const : '/v1/attribution' as const;
  async function get<T, M = Record<string, unknown>>(path: string): Promise<Envelope<T, M>> {
    let meta: M | undefined;
    const data = await bff<T>(`${base}${path}` as `/v1/${string}`, {}, async (input, init) => {
      const res = await fetchFn(input, init);
      if (res.ok && res.status !== 204) {
        try { meta = ((await res.clone().json()) as { meta?: M } | null)?.meta; } catch { /* body read by bff() */ }
      }
      return res;
    });
    return meta === undefined ? { data } : { data, meta };
  }
  return {
    kpis: (r) => get(`/kpis${qs({ from: r.fromDay, to: r.toDay, granularity: 'day' })}`),
    freshness: () => get('/freshness'),
    channels: (r) => get(`/channels${qs({ from: r.fromIso, to: r.toIso })}`),
    records: (q) => get(`/records${qs({ limit: q.limit, cursor: q.cursor })}`),
    record: (id) => get(`/records/${encodeURIComponent(id)}`),
    funnel: (r) => get(`/funnel${qs({ from: r.fromIso, to: r.toIso })}`),
  };
}


